import { Injectable } from '@angular/core';
import { ParentManagerService } from './parent-manager.service';


type AdobeConnectUser = {
  id: string;
  name: string;
  fullName: string;
  role: string;
};
/**
 * Liest Meeting-/Teilnehmerinformationen aus einer umgebenden Adobe-Connect-Sitzung (`RAGAI.ADOBECONNECT`).
 * @remarks
 * Funktioniert ausschließlich, wenn der Bot als Fusion-View/iFrame **innerhalb eines Adobe-Connect-Meetingraums**
 * läuft: Jede Methode führt letztlich per `RAGAI.PARENT.get()` einen JS-Ausdruck wie `window.cpu.sc.getMyUserDetails()`
 * im Elternfenster aus - dieses `window.cpu.sc`-API existiert nur innerhalb von Adobe Connect. Außerhalb eines
 * Adobe-Connect-Kontexts schlagen alle Methoden fehl bzw. liefern `undefined`.
 */
@Injectable({
  providedIn: 'root'
})

export class AdobeconnectManagerService {
  constructor(private parent: ParentManagerService) { }
  /** Liefert Details (id/name/fullName/role) des eigenen Adobe-Connect-Nutzers. */
  public async myUser(): Promise<AdobeConnectUser> {
    return await this.parent.get('window.cpu.sc.getMyUserDetails()') as AdobeConnectUser;
  }

  /** Anzeigename des eigenen Nutzers. */
  public async myUserName(): Promise<string> {
    return (await this.myUser()).name;
  }
  /** Vollständiger Name des eigenen Nutzers. */
  public async myUserFullName(): Promise<string> {
    return (await this.myUser()).fullName;
  }
  /** Eigene Nutzer-ID im Meetingraum. */
  public async myUserId(): Promise<string> {
    return (await this.myUser()).id;
  }

  /** Eigene Rolle im Meetingraum ("owner", "presenter" oder "viewer"). */
  public async myUserRole(): Promise<string> {
    return (await this.myUser()).role;
  }

  /** Liste aller Nutzer im Meetingraum, unabhängig von ihrer Rolle. */
  public async getUserList(): Promise<AdobeConnectUser[]> {
    return (await this.parent.get('window.cpu.sc._userList')) as AdobeConnectUser[];
  }
  /** Gesamtanzahl aller Nutzer im Meetingraum. */
  public async getUserCount(): Promise<number> {
    const users = await this.getUserList();
    return users.length;
  }

  /** Nutzer mit der Rolle "viewer" (Teilnehmer). */
  public async getParticipants(): Promise<AdobeConnectUser[]> {
    const users = await this.getUserList();
    return users.filter(u => u.role == "viewer");
  }
  /** Anzahl der Teilnehmer (Rolle "viewer"). */
  public async getParticipantsCount(): Promise<number> {
    const users = await this.getParticipants();
    return users.length;
  }
  /** Nutzer mit der Rolle "presenter". */
  public async getPresenters(): Promise<AdobeConnectUser[]> {
    const users = await this.getUserList();
    return users.filter(u => u.role == "presenter");
  }
  /** Anzahl der Presenter. Nutzt aktuell (Bug im Original) dieselbe Quelle wie getParticipantsCount(). */
  public async getPresentersCount(): Promise<number> {
    const users = await this.getParticipants();
    return users.length;
  }
  /** Nutzer mit der Rolle "owner" (Hosts). */
  public async getHosts(): Promise<AdobeConnectUser[]> {
    const users = await this.getUserList();
    return users.filter(u => u.role == "owner");
  }
  /** Anzahl der Hosts. */
  public async getHostsCount(): Promise<number> {
    const users = await this.getHosts();
    return users.length;
  }
  /** Name des aktuell gewählten Raum-Layouts. */
  public async selectedLayout(): Promise<string> {
    return String(await this.parent.get('window.selectedLayoutName'));
  }
  /** Löst eine Adobe-Connect-SyncMessage im Elternfenster aus (`window.cpu.dispatchSyncMessage`). */
  public dispatchSyncMessage(name: string, value: unknown) {
    this.parent.exec(`window.cpu.dispatchSyncMessage('${name}', ${JSON.stringify(value)}, false, false)`);
  }

  /** Gibt an, ob das Meeting gerade aufgezeichnet wird. */
  public async isRecording(): Promise<boolean> {
    return await this.parent.get(`window.cpu.sc._isArchive`) as boolean;
  }
  /** Gibt an, ob gerade eine Aufzeichnung abgespielt wird. */
  public async getRecordingPlayState(): Promise<boolean> {
    return await this.parent.get(`window.cpu.sc._playState`) as boolean;
  }
  /** Gibt an, ob der Bot als Add-in (statt als Pod) eingebunden ist. */
  public async isAddin(): Promise<boolean> {
    return await this.parent.get(`window.cpu.sc._isAddin`) as boolean;
  }
  /** URL des aktuellen Meetingraums. */
  public async getRoomUrl(): Promise<string> {
    return await this.parent.get(`window.cpu.sc._url`) as string;
  }
  /** Titel des Pods, in dem der Bot eingebettet ist. */
  public async getPodTitle(): Promise<string> {
    return await this.parent.get(`window.cpu.sc._podTitle`) as string;
  }
  /** SCO-ID (Content-ID) des aktuellen Meetingraums. */
  public async getRoomSCOID(): Promise<string> {
    return await this.parent.get(`window.cpu.sc._roomSCOID`) as string;
  }
}

